import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, UserCheck, ShieldCheck, ArrowRight, ArrowDown, Route, Fuel, Satellite, BookOpenText, Send, CheckCircle2, Landmark } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const LOGO = "https://customer-assets-jt897jd0.emergentagent.net/job_drive-smart-25/artifacts/vqfz3t07_ENFLEET%20by%20POMEN.png";

const STEPS = [
  { icon: CalendarCheck, role: "Staf", title: "Mohon Tempahan", desc: "Staf memohon kenderaan secara digital — destinasi, tujuan, tarikh & bilangan penumpang." },
  { icon: UserCheck, role: "Operator", title: "Lulus & Tugaskan", desc: "Pengendali menyemak permohonan, menugaskan kenderaan paling sesuai dengan cadangan sistem." },
  { icon: ShieldCheck, role: "Admin", title: "Kelulusan Akhir", desc: "Pentadbir memberi kelulusan akhir — terus dari emel atau papan pemuka, dengan jejak audit penuh." },
];

const DATA_POINTS = [
  { icon: Route, title: "Mula & Tamat Perjalanan", desc: "Odometer, masa bertolak & pulang direkod digital bagi setiap trip — Buku Log automatik." },
  { icon: Fuel, title: "Aktiviti Isi Minyak", desc: "Resit, jenis minyak, harga seliter & kaedah bayaran (kad minyak/tuntutan) direkod di lokasi." },
  { icon: Satellite, title: "Integrasi Telematik", desc: "Data GPS & jarak sebenar dari peranti telematik menyemak silang setiap rekod perjalanan." },
  { icon: BookOpenText, title: "Buku Log & Laporan", desc: "Penggunaan bahan api (Km/L), kos per kenderaan & laporan PDF sedia untuk audit." },
];

export default function RegisterInterest() {
  const [form, setForm] = useState({ agency_name: "", pic_name: "", email: "", phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/register-interest", form);
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="register-interest-page">
      {/* Top bar */}
      <header className="bg-[#10056E] px-6 py-4 flex items-center justify-between">
        <img src={LOGO} alt="ENFLEET by POMEN" className="h-9 w-auto" />
        <Link to="/login">
          <Button variant="outline" size="sm" className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[#10056E]" data-testid="back-to-login-link">
            Log Masuk
          </Button>
        </Link>
      </header>

      {/* Hero */}
      <section className="bg-[#10056E] text-white px-6 pt-12 pb-20 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5 blur-3xl pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative">
          <span className="inline-flex items-center gap-2 text-xs font-semibold bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <Landmark className="h-3.5 w-3.5" /> Khas untuk Agensi Kerajaan Malaysia
          </span>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
            Pengurusan Fleet & Buku Log Digital<br className="hidden sm:block" /> Dalam Satu Sistem
          </h1>
          <p className="text-white/70 text-base md:text-lg mt-4 max-w-2xl mx-auto">
            Tempahan kenderaan, kelulusan berperingkat, rekod perjalanan dan isi minyak — semuanya digital, telus dan sedia untuk audit.
          </p>
          <p className="text-white/90 text-sm mt-3 max-w-2xl mx-auto inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2">
            <ShieldCheck className="h-4 w-4 shrink-0" />
            Mengikut standard Pengurusan Kenderaan Kerajaan Malaysia — Pekeliling WP 4.1
          </p>
          <br />
          <a href="#daftar">
            <Button className="mt-7 bg-white text-[#10056E] hover:bg-slate-100 font-semibold h-11 px-7" data-testid="hero-register-btn">
              Daftar Sekarang <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Infographic */}
      <section className="px-6 pt-14 pb-2 max-w-6xl mx-auto">
        <h2 className="font-heading text-lg md:text-xl font-bold text-center tracking-tight">Bagaimana ENFLEET Berfungsi</h2>
        <p className="text-sm text-muted-foreground text-center mt-1 mb-8">Dari tempahan digital hingga laporan sedia audit — dalam 4 langkah.</p>
        <img
          src="https://customer-assets-jt897jd0.emergentagent.net/job_drive-smart-25/artifacts/cq2x04wk_ENFLEET_Pengurusan_BukuLog_Kenderaan_Digital.jpg"
          alt="Infografik ENFLEET — Pengurusan Buku Log Kenderaan Digital"
          className="w-full h-auto rounded-2xl border border-slate-200 shadow-md"
          data-testid="landing-infographic"
        />
      </section>

      {/* 3-step workflow */}
      <section className="px-6 py-16 max-w-6xl mx-auto">
        <h2 className="font-heading text-lg md:text-xl font-bold text-center tracking-tight">Aliran Kerja 3 Peringkat</h2>
        <p className="text-sm text-muted-foreground text-center mt-1 mb-10">
          Setiap tempahan melalui semakan berperingkat selaras dengan kawalan penggunaan kenderaan WP 4.1 — tiada lagi borang manual.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-stretch">
          {STEPS.map((s, i) => (
            <div key={s.role} className="contents">
              <div className="md:col-span-1 border border-slate-200 rounded-2xl p-6 text-center shadow-sm hover:shadow-md transition-shadow bg-white" data-testid={`step-card-${s.role}`}>
                <span className="h-14 w-14 rounded-2xl bg-[#10056E]/5 border border-[#10056E]/15 flex items-center justify-center mx-auto">
                  <s.icon className="h-7 w-7 text-[#10056E]" />
                </span>
                <p className="text-[11px] font-bold uppercase tracking-widest text-[#DC3545] mt-4">Langkah {i + 1} · {s.role}</p>
                <p className="font-heading font-bold mt-1">{s.title}</p>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{s.desc}</p>
              </div>
              {i < STEPS.length - 1 && (
                <div className="hidden md:flex items-center justify-center">
                  <ArrowRight className="h-7 w-7 text-[#10056E]/40" />
                </div>
              )}
              {i < STEPS.length - 1 && (
                <div className="flex md:hidden items-center justify-center">
                  <ArrowDown className="h-6 w-6 text-[#10056E]/40" />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Data collection */}
      <section className="bg-slate-50 border-y border-slate-200 px-6 py-16">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-lg md:text-xl font-bold text-center tracking-tight">Data Lengkap Dari Mula Perjalanan Hingga Laporan</h2>
          <p className="text-sm text-muted-foreground text-center mt-1 mb-10">
            Setiap kilometer, liter dan ringgit direkodkan secara automatik dan disemak silang dengan telematik.<br />
            Buku Log digital lengkap seperti dikehendaki Pekeliling WP 4.1.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {DATA_POINTS.map((d, i) => (
              <div key={d.title} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm relative" data-testid={`data-card-${i}`}>
                <span className="absolute -top-3 left-5 text-[10px] font-bold bg-[#10056E] text-white rounded-full px-2.5 py-1">{i + 1}</span>
                <d.icon className="h-6 w-6 text-[#10056E] mt-2" />
                <p className="font-heading font-bold text-sm mt-3">{d.title}</p>
                <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration form */}
      <section id="daftar" className="px-6 py-16">
        <div className="max-w-xl mx-auto">
          <h2 className="font-heading text-lg md:text-xl font-bold text-center tracking-tight">Daftar Agensi Anda</h2>
          <p className="text-sm text-muted-foreground text-center mt-1 mb-8">
            Isi maklumat di bawah — pasukan kami akan menghubungi anda untuk tindakan selanjutnya.
          </p>
          {sent ? (
            <div className="border border-green-200 bg-green-50 rounded-2xl p-8 text-center" data-testid="interest-success">
              <CheckCircle2 className="h-10 w-10 text-[#198754] mx-auto" />
              <p className="font-heading font-bold mt-3">Terima kasih! Pendaftaran anda telah dihantar.</p>
              <p className="text-sm text-muted-foreground mt-1">Pasukan kami akan menghubungi anda dalam masa terdekat.</p>
              <Link to="/login">
                <Button variant="outline" className="mt-5" data-testid="success-back-login">Kembali ke Log Masuk</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm space-y-4 bg-white">
              <div className="space-y-1.5">
                <Label>Nama Agensi</Label>
                <Input required value={form.agency_name} onChange={(e) => setForm({ ...form, agency_name: e.target.value })}
                  placeholder="Cth: Majlis Bandaraya..." data-testid="interest-agency-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Nama Pegawai (PIC)</Label>
                <Input required value={form.pic_name} onChange={(e) => setForm({ ...form, pic_name: e.target.value })}
                  data-testid="interest-pic-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Emel</Label>
                  <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="nama@agensi.gov.my" data-testid="interest-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>No. Telefon</Label>
                  <Input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="012-3456789" data-testid="interest-phone-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Nota (pilihan)</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Cth: Saiz armada, keperluan khusus agensi..." data-testid="interest-notes-input" />
              </div>
              <Button type="submit" disabled={busy} className="w-full h-11 bg-[#10056E] hover:bg-[#2a17a8]" data-testid="interest-submit-btn">
                {busy ? "Menghantar..." : <><Send className="h-4 w-4 mr-2" /> Hantar Pendaftaran</>}
              </Button>
            </form>
          )}
        </div>
      </section>

      <footer className="bg-[#10056E] text-center text-white/50 text-xs py-6">
        © {new Date().getFullYear()} ENFLEET by POMEN
      </footer>
    </div>
  );
}
